/**
 * 权限类型定义
 * 权限ID格式: {module}:{resource}:{action}
 * 
 * ⚠️ 与后端 smart_ol/utils/permission_constants.py 保持同步
 */

// 模块定义 - 与后端 PermissionModule 对应
export const PermissionModule = {
  STUDENTS: 'students',            // 学员管理
  COURSES: 'courses',              // 课程管理
  WORKS: 'works',                  // 作品管理
  ATTENDANCE: 'attendance',        // 考勤/上课记录 (前端称"上课记录")
  CLASSROOM_LOGS: 'classroom-logs', // 课堂日志
  ACCOUNTS: 'accounts',            // 账户管理
  USERS: 'users',                  // 用户管理
  SYSTEM: 'system',                // 系统管理
  REPORTS: 'reports',              // 报表管理
  AUTH: 'auth',                    // 认证模块
  PERMISSIONS: 'permissions',      // 权限管理
  UPLOAD: 'upload',                // 文件上传
  PURCHASE: 'purchase',            // 购课/账务 (前端称"账务")
  STUDENT_LINKS: 'student-links',  // 学员关联
  COMMON: 'common',                // 公共模块
};

// 资源类型
export const PermissionResource = {
  DATA: 'data',        // 数据操作
  ROUTE: 'route',      // 路由访问
  BUTTON: 'button',    // 按钮操作
};

// 操作类型
export const PermissionAction = {
  VIEW: 'view',        // 查看
  CREATE: 'create',    // 创建
  EDIT: 'edit',        // 编辑
  DELETE: 'delete',    // 删除
  EXPORT: 'export',    // 导出
  MANAGE: 'manage',    // 管理（包含所有操作）
  ACCESS: 'access',    // 访问（用于路由）
};

// 角色定义
export const UserRole = {
  ADMIN: 'admin',      // 管理员
  TEACHER: 'teacher',  // 老师
  STUDENT: 'student',  // 学生/家长
};

/**
 * 按钮级权限配置
 * ⚠️ 与后端权限格式保持一致: module:resource:action
 */
export const ButtonPermissions = {
  // 考勤/上课记录模块 (后端: attendance)
  attendance: {
    create: 'attendance:data:create',
    edit: 'attendance:data:edit',
    delete: 'attendance:data:delete',
    view: 'attendance:data:view',
  },

  // 学员管理模块
  students: {
    create: 'students:data:create',
    edit: 'students:data:edit',
    delete: 'students:data:delete',
    view: 'students:data:view',
    export: 'students:data:export',
  },

  // 课程管理模块
  courses: {
    create: 'courses:data:create',
    edit: 'courses:data:edit',
    delete: 'courses:data:delete',
    view: 'courses:data:view',
  },

  // 购课/账务模块 (后端: purchase)
  purchase: {
    create: 'purchase:data:create',
    edit: 'purchase:data:edit',
    delete: 'purchase:data:delete',
    view: 'purchase:data:view',
    export: 'purchase:data:export',
  },

  // 作品管理模块
  works: {
    create: 'works:data:create',
    edit: 'works:data:edit',
    delete: 'works:data:delete',
    view: 'works:data:view',
  },

  // 课堂日志模块
  classroomLogs: {
    create: 'classroom-logs:data:create',
    edit: 'classroom-logs:data:edit',
    delete: 'classroom-logs:data:delete',
    view: 'classroom-logs:data:view',
  },

  // 系统管理模块
  system: {
    manageUsers: 'system:user:manage',
    managePermissions: 'system:permission:manage',
  },

  // 用户管理模块
  users: {
    create: 'users:data:create',
    edit: 'users:data:edit',
    delete: 'users:data:delete',
    view: 'users:data:view',
  },

  // 报表模块
  reports: {
    view: 'reports:data:view',
    export: 'reports:data:export',
  },
};

/**
 * 路由权限配置
 * 用于控制导航菜单可见性
 */
export const RoutePermissions = {
 home: 'attendance:route:access',      // 首页 - 上课记录
  academic: 'students:route:access',    // 学术 - 学员管理
  finance: 'purchase:route:access',     // 账务
  report: 'reports:route:access',       // 报表
  works: 'works:route:access',          // 作品
  system: 'system:route:access',        // 系统设置
};

/**
 * 角色默认权限配置
 * ⚠️ 生产环境应从后端获取权限列表
 */
export const RoleDefaultPermissions = {
  // 管理员拥有所有权限
  [UserRole.ADMIN]: '*',

  // 老师/管理员权限 (对应后端 MANAGER 角色)
  [UserRole.TEACHER]: [
    // 考勤/上课记录 - 全部权限
    'attendance:data:view',
    'attendance:data:create',
    'attendance:data:edit',
    'attendance:data:delete',
    'attendance:route:access',

    // 学员管理 - 查看和编辑
    'students:data:view',
    'students:data:create',
    'students:data:edit',
    'students:route:access',

    // 课程管理 - 查看
    'courses:data:view',
    'courses:route:access',

    // 购课/账务 - 仅查看
    'purchase:data:view',
    'purchase:route:access',

    // 作品 - 全部权限
    'works:data:view',
    'works:data:create',
    'works:data:edit',
    'works:data:delete',
    'works:route:access',

    // 课堂日志
    'classroom-logs:data:view',
    'classroom-logs:data:create',
    'classroom-logs:data:edit',

    // 报表 - 查看权限
    'reports:route:access',
    'reports:data:view',
  ],

  // 学生/家长权限
  [UserRole.STUDENT]: [
    // 考勤/上课记录 - 仅查看自己的
    'attendance:data:view',
    'attendance:route:access',

    // 购课/账务 - 仅查看自己的
    'purchase:data:view',
    'purchase:route:access',

    // 作品 - 仅查看自己的
    'works:data:view',
    'works:route:access',

    // 报表 - 无权限
    // 'reports:route:access',
    // 'reports:data:view',
  ],
};
